import Groq from 'groq-sdk';
import { getApiKey } from './remoteConfig';

/**
 * Initialize Groq client with API key from Remote Config
 * @returns {Groq} Groq client instance
 */
const initializeGroq = () => {
    const apiKey = getApiKey();

    if (!apiKey || apiKey === 'default-api-key-not-set') {
        console.error('❌ Groq API key not configured in Remote Config');
        throw new Error('Groq API key not configured. Please set it in Firebase Remote Config.');
    }

    console.log('🤖 Initializing Groq client...');
    return new Groq({
        apiKey: apiKey,
        dangerouslyAllowBrowser: true // Required for browser usage
    });
};

/**
 * Convert image file to base64 data URL
 * @param {File} imageFile - The image file
 * @returns {Promise<string>} Base64 data URL
 */
const imageToDataURL = (imageFile) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(imageFile);
    });
};

/**
 * Solve math problem directly from image using Groq Vision AI
 * @param {File} imageFile - The uploaded image file
 * @param {Function} onChunk - Callback for streaming response chunks
 * @returns {Promise<Object>} Solution object with problem, steps, and answer
 */
export const solveMathProblemFromImage = async (imageFile, onChunk = null) => {
    try {
        console.log('🔍 Analyzing math problem from image with Groq Vision AI...');

        const groq = initializeGroq();

        // Convert image to base64 data URL
        console.log('� Converting image to base64...');
        const imageDataURL = await imageToDataURL(imageFile);

        const prompt = `You are a specialized math tutor AI. You ONLY solve mathematical problems. You must REJECT any non-math questions.

IMPORTANT RULES:
1. ONLY answer mathematics questions (algebra, calculus, geometry, trigonometry, statistics, etc.)
2. If the image does NOT contain a math problem, respond with: {"problem": "Not a math problem", "steps": ["This image does not contain a mathematical problem. I can only help with math problems."], "answer": "Please provide an image with a math problem"}
3. Do NOT answer questions about: general knowledge, coding, writing, history, science (unless it's a math problem in a science context), or any other non-math topics

If this IS a math problem, analyze the image and provide your response in this EXACT JSON format:
{
  "problem": "clearly restated math problem from the image",
  "steps": ["detailed step 1 with explanation", "detailed step 2 with explanation", "..."],
  "answer": "final numerical or algebraic answer"
}

Analyze the math problem in the image and solve it step by step.

Remember: If this is NOT a math problem, reject it immediately using the rejection format above.`;

        const chatCompletion = await groq.chat.completions.create({
            messages: [
                {
                    role: "system",
                    content: "You are a specialized math tutor AI. You ONLY solve mathematical problems. REJECT any non-math questions immediately. Only answer questions about: algebra, calculus, geometry, trigonometry, statistics, arithmetic, and other pure mathematics topics."
                },
                {
                    role: "user",
                    content: [
                        {
                            type: "text",
                            text: prompt
                        },
                        {
                            type: "image_url",
                            image_url: {
                                url: imageDataURL
                            }
                        }
                    ]
                }
            ],
            model: "meta-llama/llama-4-maverick-17b-128e-instruct", // Vision model
            temperature: 0.3, // Lower temperature for consistent math solutions
            max_completion_tokens: 8192,
            top_p: 1,
            stream: true,
            stop: null
        });

        let fullResponse = '';

        console.log('🤖 Receiving AI response...');

        // Process streaming response
        for await (const chunk of chatCompletion) {
            const content = chunk.choices[0]?.delta?.content || '';
            fullResponse += content;

            // Call onChunk callback if provided (for real-time display)
            if (onChunk && content) {
                onChunk(content);
            }
        }

        console.log('✅ Groq Vision AI response received');
        console.log('📄 Full response:', fullResponse);

        // Try to parse JSON response
        try {
            // Extract JSON from response (in case there's extra text)
            const jsonMatch = fullResponse.match(/\{[\s\S]*\}/);
            if (jsonMatch) {
                const solution = JSON.parse(jsonMatch[0]);
                console.log('✅ Solution parsed successfully:', solution);
                return solution;
            } else {
                // Fallback: create structured response from plain text
                return {
                    problem: "Math problem from image",
                    steps: [fullResponse],
                    answer: "See solution above"
                };
            }
        } catch (parseError) {
            console.warn('⚠️ Could not parse JSON, using raw response');
            // Return raw response in a structured format
            return {
                problem: "Math problem from image",
                steps: [fullResponse],
                answer: "See solution above"
            };
        }

    } catch (error) {
        console.error('❌ Error solving math problem from image:', error);
        throw error;
    }
};

/**
 * Complete flow: Solve math problem directly from image
 * @param {File} imageFile - The uploaded image file
 * @param {Function} onChunk - Callback for streaming response chunks
 * @returns {Promise<Object>} Solution object
 */
export const solveFromImage = async (imageFile, onChunk = null) => {
    try {
        console.log('🚀 Starting math problem solving from image...');

        // Solve the problem directly from image using vision model
        const solution = await solveMathProblemFromImage(imageFile, onChunk);

        console.log('🎉 Math problem solved successfully!');
        return solution;

    } catch (error) {
        console.error('❌ Error in solve flow:', error);
        throw error;
    }
};

export default {
    solveMathProblemFromImage,
    solveFromImage
};
